import { BaseSyntheticEvent, useEffect, useState } from 'react';
import axios from 'axios';
import './UsersEdit.scss';
import { Button, FormControl, FormHelperText, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { AuthHeader } from '../../../utils/helpers/auth-headers';
import { setAlertData } from '../../../stores/alertStore';
import { AlertSeverity } from '../../../utils/enum/Alerts';

interface User {
  _id: string;
  name: string;
  email: string;
  role: string;
}

function UsersEditForm({ selectedUser }: any) {
  const { t } = useTranslation();

  const { userId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const initError = {
    nameError: false,
    nameHelper: '',
    emailError: false,
    emailHelper: '',
    roleError: false,
    roleHelper: '',
  };
  const [hasError, setHasError] = useState(initError);

  const [user, setUser] = useState<User>(selectedUser);

  useEffect(() => {
    axios.get(`${window._env_.REACT_APP_API_URI}/user/${userId}`, { headers: AuthHeader() ? { Authorization: `Bearer ${AuthHeader()}` } : {} })
      .then((response) => {
        setUser(response.data.data);
      })
      .catch((error) => {
        if (error.response) {
          const err = error.response;
          console.error(err);
          navigate('/configuration/users');
        }
        console.error(error);
      });
  }, [userId, navigate]);

  const handleChange = (event: any) => {
    setUser({ ...user, [event.target.name]: event.target.value });
  };

  const validateEmail = (email: string) => email.match(
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  );

  const editUser = () => {
    axios.patch(`${window._env_.REACT_APP_API_URI}/user/${userId}`, user, { headers: AuthHeader() ? { Authorization: `Bearer ${AuthHeader()}` } : {} })
      .then(() => {
        navigate('/configuration/users');
      })
      .catch((error) => {
        if (error.response) {
          const err = error.response;
          console.error(err);
          dispatch(setAlertData({
            message: err.data.message,
            severity: AlertSeverity.ERROR,
            open: true,
          }));
        }
        console.error(error);
      });
  };

  const onEditUser = (event: BaseSyntheticEvent) => {
    event.preventDefault();
    const tempError = { ...initError };
    if (user.name === '') {
      tempError.nameError = true;
      tempError.nameHelper = 'UsersEditForm.nameRequired';
    }
    if (user.email === '') {
      tempError.emailError = true;
      tempError.emailHelper = 'UsersEditForm.emailRequired';
    }
    if (!validateEmail(user.email)) {
      tempError.emailError = true;
      tempError.emailHelper = 'UsersEditForm.wrongFormat';
    }
    if (user.role === '') {
      tempError.roleError = true;
      tempError.roleHelper = 'UsersEditForm.roleRequired';
    }
    setHasError(tempError);
    if (_.isEqual(tempError, initError)) {
      editUser();
    }
  };

  const cancelEdit = () => {
    navigate('/configuration/users');
  };

  return (
    <div>
      <form onSubmit={onEditUser} className="form">
        <TextField
          variant="outlined"
          label={t('UsersEditForm.name')}
          error={hasError.nameError}
          helperText={hasError.nameError ? t(hasError.nameHelper) : ''}
          value={user.name}
          onChange={handleChange}
          type="text"
          name="name"
          required
        />
        <TextField
          variant="outlined"
          label={t('UsersEditForm.email')}
          error={hasError.emailError}
          helperText={hasError.emailError ? t(hasError.emailHelper) : ''}
          value={user.email}
          onChange={handleChange}
          type="email"
          name="email"
          placeholder="Ex. pat@example.com"
          required
        />
        <FormControl fullWidth error={hasError.roleError} required>
          <InputLabel id="role-select-label">{ t('UsersEditForm.role') }</InputLabel>
          <Select
            labelId="role-select-label"
            id="role-simple-select"
            value={user.role}
            label={t('UsersEditForm.role')}
            name="role"
            onChange={handleChange}
            required
          >
            <MenuItem value="admin">Admin</MenuItem>
            <MenuItem value="user">User</MenuItem>
          </Select>
          <FormHelperText>{hasError.roleError ? t(hasError.roleHelper) : ''}</FormHelperText>
        </FormControl>
        <Stack direction="row" spacing={2} alignSelf="center" width="100%">
          <Button fullWidth onClick={cancelEdit} variant="outlined">{ t('UsersEditForm.cancel') }</Button>
          <Button fullWidth type="submit" value="submit" variant="contained">{ t('UsersEditForm.submit') }</Button>
        </Stack>
      </form>
    </div>
  );
}

export default UsersEditForm;
